import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Chip,
  useTheme,
  alpha,
  IconButton,
  InputAdornment,
  DialogContentText,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { VolunteerLogEntry } from '@/services/api/domains/volunteer';
import { useToast } from '@/components/common/Toast';
import { formInputStyles, numberInputProps } from '@/styles/formStyles';

interface VolunteerLogModalProps {
  open: boolean;
  onClose: () => void;
  log: VolunteerLogEntry | null;
  onUpdate: (logId: number, hours: number, description: string) => Promise<void>;
  onDelete: (logId: number) => Promise<void>;
  onSuccess: () => void;
}

export const VolunteerLogModal: React.FC<VolunteerLogModalProps> = ({
  open,
  onClose,
  log,
  onUpdate,
  onDelete,
  onSuccess,
}) => {
  const toast = useToast();
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [hoursError, setHoursError] = useState('');
  const [hoursFieldTouched, setHoursFieldTouched] = useState(false);

  // Reset state when log changes
  useEffect(() => {
    if (log) {
      console.log('Log data received:', {
        log,
        opportunity: log.opportunity,
        hours: log.hours,
        remainingHours: log.opportunity.hours_remaining,
        commitmentHours: log.opportunity.commitment_hours
      });
      setHours(log.hours.toString());
      setDescription(log.description);
      setHoursError('');
      setHoursFieldTouched(false);
    }
  }, [log]);

  const handleEdit = () => {
    if (!log) return;
    setHours(log.hours.toString());
    setDescription(log.description);
    setIsEditing(true);
  };

  const handleHoursChange = (value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setHours(value);
      validateHours(value);
    }
  };

  const handleHoursBlur = () => {
    setHoursFieldTouched(true);
    validateHours(hours);
  };

  const validateHours = (value: string): boolean => {
    if (!value || value === '') {
      if (hoursFieldTouched) {
        setHoursError('Please enter the number of hours');
        return false;
      }
      return true;
    }

    const numValue = parseFloat(value);
    
    if (isNaN(numValue) || numValue <= 0) {
      setHoursError('Hours must be greater than 0');
      return false;
    }

    if (!log?.opportunity) {
      setHoursError('Opportunity data is missing');
      return false;
    }

    const availableHours = getAvailableHours(log);
    if (availableHours === null) {
      setHoursError('Unable to validate hours - data is incomplete');
      return false;
    }

    if (numValue > availableHours) {
      setHoursError(`You can only log up to ${availableHours} hours for this opportunity`);
      return false;
    }

    setHoursError('');
    return true;
  };

  const handleUpdate = async () => {
    if (!log) return;

    if (!validateHours(hours)) {
      return;
    }

    try {
      setIsPending(true);
      await onUpdate(log.id, parseFloat(hours), description);
      toast.showSuccess('Volunteer log updated successfully!');
      onSuccess();
      setIsEditing(false);
      handleClose();
    } catch (error) {
      console.error('Failed to update log:', error);
      toast.showError(error);
    } finally {
      setIsPending(false);
    }
  };

  const handleDelete = async () => {
    if (!log) return;

    try {
      setIsPending(true);
      await onDelete(log.id);
      toast.showSuccess('Volunteer log deleted successfully!');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to delete log:', error);
      toast.showError(error);
    } finally {
      setIsPending(false);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    setIsDeleting(false);
    setHours('');
    setDescription('');
    onClose();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return theme.palette.success;
      case 'pending':
        return theme.palette.warning;
      case 'rejected':
        return theme.palette.error;
      default:
        return theme.palette.info;
    }
  };

  const getAvailableHours = (log: VolunteerLogEntry): number | null => {
    console.log('getAvailableHours called with log:', {
      logId: log?.id,
      opportunity: log?.opportunity,
      hours: log?.hours
    });

    if (!log?.opportunity) {
      console.warn('No opportunity data available');
      return null;
    }
    
    const { hours_remaining } = log.opportunity;

    console.log('Hours data extracted:', {
      hours_remaining,
      hours_remaining_type: typeof hours_remaining,
      isNaN_hours_remaining: isNaN(hours_remaining)
    });

    // Validate that we have the required number and it is valid
    if (typeof hours_remaining !== 'number' || isNaN(hours_remaining)) {
      console.warn('Invalid hours data:', { hours_remaining });
      return null;
    }

    return hours_remaining;
  };

  const formatHoursDisplay = (log: VolunteerLogEntry): string => {
    console.log('formatHoursDisplay called with log:', {
      logId: log?.id,
      opportunity: log?.opportunity,
      hours: log?.hours
    });

    if (!log?.opportunity) {
      console.warn('formatHoursDisplay: No opportunity data');
      return 'Hours data unavailable';
    }

    const { commitment_hours, hours_remaining } = log.opportunity;
    const availableHours = getAvailableHours(log);

    console.log('Hours display data:', {
      commitment_hours,
      hours_remaining,
      availableHours,
      commitment_hours_type: typeof commitment_hours,
      hours_remaining_type: typeof hours_remaining,
      isNaN_commitment_hours: isNaN(commitment_hours),
      isNaN_hours_remaining: isNaN(hours_remaining)
    });

    if (availableHours === null || 
        typeof commitment_hours !== 'number' || 
        typeof hours_remaining !== 'number' ||
        isNaN(commitment_hours) || 
        isNaN(hours_remaining)) {
      console.warn('formatHoursDisplay: Invalid data detected', {
        availableHours,
        commitment_hours,
        hours_remaining
      });
      return 'Hours data unavailable';
    }

    return `${availableHours} hours available for this opportunity`;
  };

  if (!log) return null;

  const statusColor = getStatusColor(log.status);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...formInputStyles(theme),
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          <Box component="div">
            <Typography variant="h6">Volunteer Log Details</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {isDeleting ? (
            <Box sx={{ py: 2 }}>
              <DialogContentText>
                Are you sure you want to delete this volunteer log? This action cannot be undone.
              </DialogContentText>
            </Box>
          ) : (
            <Stack spacing={3} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={log.status}
                  size="small"
                  sx={{
                    mt: 0.5,
                    bgcolor: alpha(statusColor.main, 0.1),
                    color: statusColor.main,
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Opportunity
                </Typography>
                <Typography variant="body1">
                  {log.opportunity.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {log.opportunity.organization}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="body1">
                  {format(new Date(log.date), 'MMMM d, yyyy')}
                </Typography>
              </Box>

              {isEditing ? (
                <>
                  <TextField
                    fullWidth
                    label="Hours"
                    type="number"
                    value={hours}
                    onChange={(e) => handleHoursChange(e.target.value)}
                    onBlur={handleHoursBlur}
                    error={!!hoursError}
                    helperText={hoursError || (log && formatHoursDisplay(log))}
                    required
                    sx={formInputStyles(theme)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TimeIcon color={hoursError ? "error" : "action"} />
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...numberInputProps,
                        min: 0,
                        step: 0.5,
                        max: log ? getAvailableHours(log) ?? undefined : undefined
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              ) : (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Hours
                    </Typography>
                    <Typography variant="body1">
                      {log.hours}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {log.description}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {isDeleting ? (
            <>
              <Button onClick={() => setIsDeleting(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={isPending}
                variant="contained"
                color="error"
              >
                {isPending ? 'Deleting...' : 'Confirm Delete'}
              </Button>
            </>
          ) : isEditing ? (
            <>
              <Button onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={isPending}
                variant="contained"
              >
                {isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setIsDeleting(true)}
                color="error"
                startIcon={<DeleteIcon />}
              >
                Delete
              </Button>
              <Button
                onClick={handleEdit}
                variant="contained"
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}; 